/**
 * Delivery-side media optimization.
 * Rewrites image/video URLs to go through transform endpoints that
 * resize, re-encode, and serve modern formats (WebP/AVIF) automatically.
 *
 * - Supabase Storage URLs use the built-in render endpoint.
 * - External http(s) images are proxied through wsrv.nl (free image CDN).
 * - data:, blob:, and local assets are returned unchanged.
 */

const SUPABASE_STORAGE_RE = /\/storage\/v1\/object\/(public|sign|authenticated)\//;

export interface ImageTransformOpts {
  /** Target rendered width in CSS pixels (will be DPR-scaled). */
  width?: number;
  /** Target rendered height in CSS pixels. */
  height?: number;
  /** 1-100. Defaults to 95 (visually lossless). */
  quality?: number;
}

function shouldSkip(url: string | undefined | null): url is undefined {
  if (!url) return true;
  if (url.startsWith("data:") || url.startsWith("blob:")) return true;
  // Local assets bundled by Vite — already hashed/optimized.
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  // SVGs are vector; transforming wastes resources.
  if (/\.svgz?(\?|$)/i.test(url)) return true;
  return false;
}

function dpr(): number {
  if (typeof window === "undefined") return 1;
  return Math.min(window.devicePixelRatio || 1, 2);
}

/**
 * Returns an optimized URL for the given image, or the original URL when
 * transformation is not applicable.
 */
export function optimizedImageUrl(url: string | undefined | null, opts: ImageTransformOpts = {}): string {
  if (shouldSkip(url)) return url ?? "";
  const src = url as string;
  const quality = Math.max(20, Math.min(100, opts.quality ?? 95));
  const w = opts.width ? Math.round(opts.width * dpr()) : undefined;
  const h = opts.height ? Math.round(opts.height * dpr()) : undefined;

  try {
    // Supabase Storage public/sign URLs → use render/image transform.
    if (SUPABASE_STORAGE_RE.test(src)) {
      const transformed = src.replace(SUPABASE_STORAGE_RE, "/storage/v1/render/image/$1/");
      const u = new URL(transformed);
      if (w) u.searchParams.set("width", String(w));
      if (h) u.searchParams.set("height", String(h));
      u.searchParams.set("quality", String(quality));
      u.searchParams.set("resize", "contain");
      return u.toString();
    }

    // External http(s) → proxy through wsrv.nl with WebP/AVIF auto-negotiation.
    if (/^https?:\/\//i.test(src)) {
      const stripped = src.replace(/^https?:\/\//i, "");
      const params = new URLSearchParams();
      params.set("url", stripped);
      if (w) params.set("w", String(w));
      if (h) params.set("h", String(h));
      params.set("q", String(quality));
      params.set("output", "webp");
      params.set("we", ""); // do not enlarge
      return `https://wsrv.nl/?${params.toString()}`;
    }
  } catch {
    /* fall through to original */
  }
  return src;
}

/**
 * Build a srcset across common widths so the browser picks the best size.
 */
export function optimizedSrcSet(url: string | undefined | null, widths: number[] = [320, 640, 960, 1280, 1920]): string {
  if (shouldSkip(url)) return "";
  return widths
    .map((w) => `${optimizedImageUrl(url, { width: w })} ${w}w`)
    .join(", ");
}

/**
 * Returns a poster (single-frame) image URL for a video.
 * Supabase storage videos have no built-in thumbnail, so we fall back to
 * the original URL — the <video> element will decode the first frame itself.
 */
export function videoPosterUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  return undefined; // Browser-native poster generation via preload="metadata".
}